// Die Anmeldeseite: drei Karten – Anmelden, Neues Konto, Passwort vergessen. Sie ist das Erste,
// was ohne gültige Sitzung zu sehen ist; eine Gastansicht gibt es nicht
// (docs/architektur-paket-2.md, Abschnitt „Client (auth/)“).
//
// Hier steht nur DOM: Jede Regel steckt in regeln.ts, jeder Aufruf in api.ts, der Ablauf in
// zustand.ts. Diese Datei baut Felder, zeigt Meldungen an der Karte, wo sie entstehen, und ruft
// `beiErfolg`. Bedienung ohne Maus: Jede Karte ist ein `form`, Tab läuft der Reihe nach durch die
// Felder, Enter schickt die Karte ab, in der der Cursor steht.
//
// Passwörter und Codes werden nie geloggt und stehen in keiner Meldung.
import {
  sendeAnmelden,
  sendePasswortNeu,
  sendeRegistrieren,
  type ApiAntwort,
  type HolFunktion,
  type IchDaten,
} from './api.js';
import { pruefeAnmeldung, pruefeNeuesKonto, pruefeNeuesPasswort, type Pruefung } from './regeln.js';

export interface AnmeldeseiteOptionen {
  hol: HolFunktion;
  /** Kurzer Satz über den Karten, etwa „Du wurdest woanders angemeldet.“ */
  hinweis?: string | null;
  /** Anmelden, Registrieren oder Passwort-neu hat geklappt; die Sitzung steht. */
  beiErfolg(ich: IchDaten): void;
}

export interface Anmeldeseite {
  /** Nimmt die Seite wieder aus dem Dokument. */
  entferne(): void;
}

/** Ein Eingabefeld einer Karte. */
interface FeldPlan {
  schluessel: string;
  label: string;
  typ: 'text' | 'password';
  /** Hinweis für den Browser, was hier hingehört (Passwortverwaltung, Ausfüllhilfe). */
  autocomplete: string;
  /** Kurze Hilfe unter dem Feld, wo sie Kindern das Rätseln erspart. */
  hilfe?: string;
}

/** Eine fertige Karte mit ihren Feldern, ihrer Meldung und ihrem Knopf. */
interface Karte {
  form: HTMLFormElement;
  werte(): Record<string, string>;
  setzeFehler(text: string | null): void;
  setzeWartet(wartet: boolean): void;
  felder: Map<string, HTMLInputElement>;
}

export function zeigeAnmeldeseite(parent: HTMLElement, optionen: AnmeldeseiteOptionen): Anmeldeseite {
  const wurzel = document.createElement('div');
  wurzel.className = 'auth';

  const kopf = document.createElement('div');
  kopf.className = 'auth-kopf';
  const titel = document.createElement('h1');
  titel.textContent = 'Bauernhof';
  const unterzeile = document.createElement('p');
  unterzeile.className = 'auth-unterzeile';
  unterzeile.textContent = 'Melde dich an, dann geht es auf den Hof.';
  kopf.append(titel, unterzeile);

  if (optionen.hinweis !== null && optionen.hinweis !== undefined && optionen.hinweis !== '') {
    const hinweis = document.createElement('p');
    hinweis.className = 'auth-hinweis';
    hinweis.setAttribute('role', 'status');
    hinweis.textContent = optionen.hinweis;
    kopf.append(hinweis);
  }

  const karten = document.createElement('div');
  karten.className = 'auth-karten';

  const anmelden = baueKarte('Anmelden', 'Los geht’s', [
    { schluessel: 'name', label: 'Spielername', typ: 'text', autocomplete: 'username' },
    { schluessel: 'passwort', label: 'Passwort', typ: 'password', autocomplete: 'current-password' },
  ]);

  const neuesKonto = baueKarte('Neues Konto', 'Konto anlegen', [
    { schluessel: 'name', label: 'Spielername', typ: 'text', autocomplete: 'username', hilfe: 'Drei bis sechzehn Buchstaben oder Ziffern.' },
    { schluessel: 'passwort', label: 'Passwort', typ: 'password', autocomplete: 'new-password', hilfe: 'Mindestens sechs Zeichen. Merke es dir gut.' },
    { schluessel: 'einladungscode', label: 'Einladungscode', typ: 'text', autocomplete: 'off', hilfe: 'Steht auf dem Zettel von dem, der das Spiel betreut.' },
  ]);

  const passwortVergessen = baueKarte('Passwort vergessen', 'Passwort neu setzen', [
    { schluessel: 'name', label: 'Spielername', typ: 'text', autocomplete: 'username' },
    { schluessel: 'code', label: 'Rücksetzcode', typ: 'text', autocomplete: 'off', hilfe: 'Auch der kommt von dem, der das Spiel betreut.' },
    { schluessel: 'passwort', label: 'Neues Passwort', typ: 'password', autocomplete: 'new-password', hilfe: 'Mindestens sechs Zeichen.' },
  ]);

  karten.append(anmelden.form, neuesKonto.form, passwortVergessen.form);
  wurzel.append(kopf, karten);
  parent.append(wurzel);

  hänge(
    anmelden,
    (werte) => pruefeAnmeldung({ name: werte['name'] ?? '', passwort: werte['passwort'] ?? '' }),
    (werte) => sendeAnmelden(optionen.hol, werte),
    optionen.beiErfolg,
  );
  hänge(
    neuesKonto,
    (werte) =>
      pruefeNeuesKonto({
        name: werte['name'] ?? '',
        passwort: werte['passwort'] ?? '',
        einladungscode: werte['einladungscode'] ?? '',
      }),
    (werte) => sendeRegistrieren(optionen.hol, werte),
    optionen.beiErfolg,
  );
  hänge(
    passwortVergessen,
    (werte) =>
      pruefeNeuesPasswort({
        name: werte['name'] ?? '',
        code: werte['code'] ?? '',
        passwort: werte['passwort'] ?? '',
      }),
    (werte) => sendePasswortNeu(optionen.hol, werte),
    optionen.beiErfolg,
  );

  // Der Cursor steht gleich im ersten Feld: Wer nur tippen will, muss nicht erst klicken.
  anmelden.felder.get('name')?.focus();

  return {
    entferne() {
      wurzel.remove();
    },
  };
}

/**
 * Verbindet eine Karte mit ihrer Prüfung und ihrem Aufruf: prüfen, absenden, Meldung zeigen oder
 * `beiErfolg` melden. Während der Aufruf läuft, ist der Knopf gesperrt, damit ein doppeltes Enter
 * nicht zwei Konten anlegt.
 */
function hänge<T extends object>(
  karte: Karte,
  pruefe: (werte: Record<string, string>) => Pruefung<T>,
  sende: (werte: T) => Promise<ApiAntwort<IchDaten>>,
  beiErfolg: (ich: IchDaten) => void,
): void {
  let laeuft = false;
  const absenden = async (): Promise<void> => {
    if (laeuft) return;
    karte.setzeFehler(null);
    const gepruefte = pruefe(karte.werte());
    if (!gepruefte.ok) {
      karte.setzeFehler(gepruefte.fehler);
      return;
    }
    laeuft = true;
    karte.setzeWartet(true);
    try {
      const antwort = await sende(gepruefte.werte);
      if (antwort.ok) beiErfolg(antwort.wert);
      else karte.setzeFehler(antwort.fehler);
    } finally {
      laeuft = false;
      karte.setzeWartet(false);
    }
  };

  karte.form.addEventListener('submit', (ereignis) => {
    ereignis.preventDefault();
    void absenden();
  });
}

/** Baut eine Karte: Überschrift, Felder mit Beschriftung, Meldung, Knopf. */
function baueKarte(titel: string, knopfText: string, plan: FeldPlan[]): Karte {
  const form = document.createElement('form');
  form.className = 'auth-karte';
  form.noValidate = true;

  const h2 = document.createElement('h2');
  h2.textContent = titel;
  form.append(h2);

  const felder = new Map<string, HTMLInputElement>();
  for (const feld of plan) {
    const label = document.createElement('label');
    label.className = 'auth-feld';
    const text = document.createElement('span');
    text.textContent = feld.label;
    const eingabe = document.createElement('input');
    eingabe.type = feld.typ;
    eingabe.name = feld.schluessel;
    // setAttribute statt der Eigenschaft: Die Typen des DOM erlauben dort nur eine feste Liste.
    eingabe.setAttribute('autocomplete', feld.autocomplete);
    eingabe.spellcheck = false;
    label.append(text, eingabe);
    if (feld.hilfe !== undefined) {
      const hilfe = document.createElement('span');
      hilfe.className = 'auth-hilfe';
      hilfe.textContent = feld.hilfe;
      label.append(hilfe);
    }
    form.append(label);
    felder.set(feld.schluessel, eingabe);
  }

  const fehler = document.createElement('p');
  fehler.className = 'auth-fehler';
  fehler.setAttribute('role', 'alert');
  fehler.hidden = true;
  form.append(fehler);

  const knopf = document.createElement('button');
  knopf.type = 'submit';
  knopf.className = 'auth-knopf';
  knopf.textContent = knopfText;
  form.append(knopf);

  return {
    form,
    felder,
    werte() {
      const werte: Record<string, string> = {};
      for (const [schluessel, eingabe] of felder) werte[schluessel] = eingabe.value;
      return werte;
    },
    setzeFehler(text) {
      fehler.textContent = text ?? '';
      fehler.hidden = text === null;
    },
    setzeWartet(wartet) {
      knopf.disabled = wartet;
      knopf.textContent = wartet ? 'Einen Moment …' : knopfText;
    },
  };
}
